import { useEffect, useState } from 'react';
import { asset } from './Brand';
import { Icon } from './Icon';

/** The browser's install event. Not in the DOM typings: it is Chromium-only. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const INSTALL_DISMISSED_KEY = 'map.install-dismissed';
/** Asking again after "not now" is fine; asking every visit is nagging. */
const QUIET_DAYS = 30;
/** Let the page settle first — a banner on the first frame reads as an ad. */
const SHOW_AFTER_MS = 2500;

/*
 * Captured at import time, not in an effect: Chromium can fire the event
 * before React has mounted anything, and a listener added afterwards never
 * sees it — the install button would then silently never appear.
 */
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppress the browser's own mini-infobar; we offer it at a better moment.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((fn) => fn());
  });
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** iPhone, iPod, and iPad — which reports itself as a Mac with a touch screen. */
function isIOS() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function recentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(INSTALL_DISMISSED_KEY));
    return at > 0 && Date.now() - at < QUIET_DAYS * 864e5;
  } catch {
    return false; // storage blocked: fall back to asking, which is harmless
  }
}

function rememberDismissal() {
  try { localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now())); } catch { /* best effort */ }
}

/**
 * Offers to put Hazelled on the home screen, on phones and tablets only.
 *
 * Two platforms, two honest paths:
 * - Chromium (Android): the browser hands us an install prompt; one tap runs it.
 * - iOS/iPadOS: there is no install API at all, so the only truthful offer is
 *   the two steps the person has to take themselves.
 * Anywhere else — desktop, or a mobile browser that supports neither — it says
 * nothing rather than promise something that will not work.
 *
 * It never takes focus and never blocks the page: it is a region someone can
 * ignore. It is withheld during a test, where the screen belongs to the question.
 */
export function InstallPrompt({ hidden = false }: { hidden?: boolean }) {
  const [canPrompt, setCanPrompt] = useState(() => deferred !== null);
  const [ready, setReady] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const sync = () => setCanPrompt(deferred !== null);
    listeners.add(sync);
    sync();
    const t = window.setTimeout(() => setReady(true), SHOW_AFTER_MS);
    return () => { listeners.delete(sync); window.clearTimeout(t); };
  }, []);

  const touch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const ios = typeof window !== 'undefined' && isIOS();
  const eligible = touch && !isStandalone() && !recentlyDismissed() && (canPrompt || ios);

  if (hidden || closed || !ready || !eligible) return null;

  function dismiss() {
    rememberDismissal();
    setClosed(true);
  }

  async function install() {
    const e = deferred;
    if (!e) return;
    deferred = null; // the event is single-use
    await e.prompt();
    const { outcome } = await e.userChoice;
    if (outcome === 'dismissed') rememberDismissal();
    setClosed(true);
  }

  return (
    <aside className="install-prompt" aria-labelledby="install-title">
      <img className="install-prompt__icon" src={asset('apple-touch-icon.png')} alt="" width={48} height={48} />
      <div className="install-prompt__body">
        <h2 id="install-title">Add Hazelled to your home screen</h2>
        {canPrompt ? (
          <p>Open it like an app — full screen, one tap from your home screen.</p>
        ) : (
          <p>
            Tap <span className="install-prompt__key"><Icon name="share" />Share</span>
            {' '}in the browser bar, then <strong>Add to Home Screen</strong>.
          </p>
        )}
        <div className="install-prompt__actions">
          {canPrompt && (
            <button className="btn-primary" onClick={() => void install()}>
              <Icon name="download" />Install
            </button>
          )}
          <button className="btn-ghost" onClick={dismiss}>{canPrompt ? 'Not now' : 'Got it'}</button>
        </div>
      </div>
      <button className="install-prompt__close" onClick={dismiss} aria-label="Dismiss install suggestion">
        <Icon name="x" />
      </button>
    </aside>
  );
}
